import { useMediaQuery } from '@mui/material'
import cx from 'classnames'
import React, { useEffect } from 'react'

import { UA } from '@js/common/UA'

import CTA from '@f/elements/CTA'
import { Form } from '@f/elements/HookForm'
import yup from '@f/elements/HookForm/validator/yup'

import { conversionFunnelCopy } from '@f-fertighaus/applications/RequestWizard/partials/utils'
import {
  WIZARD_REQUEST_TYPES,
  WizardRequestType
} from '@f-fertighaus/applications/RequestWizard/partials/wizardConstants'
import { $TSFixMe } from '@f-fertighaus/applications/UserAccount/interfaces/utils'

import { Data, User } from './dataType'

interface UserEmailPhoneProps {
  actions: $TSFixMe
  data: Data
  user?: User
  requestType: WizardRequestType
}

interface UserEmailPhoneSchema {
  email: string
  phone: string
  phoneCountryPrefix: string
  receive_newsletter?: boolean
}

const validationSchema: yup.ObjectSchema<UserEmailPhoneSchema> = yup
  .object()
  .shape({
    email: yup.string().emailCustom(),
    phone: yup.string().phone(),
    receive_newsletter: yup.boolean(),
    phoneCountryPrefix: yup.string().required()
  })

const UserEmailPhone = (props: UserEmailPhoneProps) => {
  useEffect(() => {
    props.actions.update_wtValue({ value: 'email-phone-newsletter' })
  }, [])
  const isMobileView = useMediaQuery('(max-width: 767px)')

  const {
    email,
    phone,
    phone_country_prefix,
    receive_newsletter
  } = props.data.user

  const handleSubmit = (values: UserEmailPhoneSchema) => {
    const detail = {
      email: values.email,
      phone: values.phone,
      receive_newsletter: values.receive_newsletter,
      phone_country_prefix: values.phoneCountryPrefix
    }

    props.actions.handleUserDataChange({ detail })
    props.actions.move({})
  }

  const ctaText =
    props.requestType !== WIZARD_REQUEST_TYPES.outfitter
      ? conversionFunnelCopy['vendor-wizard-item'].step_2.ctaText
      : 'Weiter'

  const disclaimerParagraph = (
    <p
      className={cx(
        '[ f-t-centre f-t-micro f-c-fo-neu200 ]',
        isMobileView ? 'f-m-v-010' : 'f-m-b-010 f-m-t-040'
      )}>
      {`Mit einem Klick auf den Button „${ctaText}“ bestätigen Sie, dass Sie unsere `}
      <CTA
        type='text-primary'
        tag='a'
        href='/datenschutz/'
        target='_blank'
        className='internal'>
        Datenschutzerklärung
      </CTA>{' '}
      zur Kenntnis genommen haben. Kein Spam, versprochen.
    </p>
  )

  return (
    <Form.Wrapper<UserEmailPhoneSchema>
      validationSchema={validationSchema}
      defaultValues={{
        email: email,
        phone: phone?.toString(),
        receive_newsletter: receive_newsletter || false,
        phoneCountryPrefix: phone_country_prefix || 'DE'
      }}
      className='RequestWizard-form-wrapper'
      onSubmit={handleSubmit}
      wtData={{
        form: {
          location: 'modal-email-phone-newsletter',
          ...props.data.trackingInfo
        },
        formSubmit: {
          location: 'modal-email-phone-newsletter',
          indicates: ['activation-handover-consent'],
          ...props.data.trackingInfo
        }
      }}
      data-wtloc='modal-email-phone-newsletter'>
      <div className='RequestWizard-top'>
        <div className='RequestWizard-heading'>
          <div className='[ f-t-tmedium f-c-fo-pri f-t-centre f-p-h-080:m ]'>
            {conversionFunnelCopy['vendor-wizard-item'].step_2.header}
          </div>
        </div>

        <div className='RequestWizard-content'>
          <Form.Text
            name='email'
            className='[ f-m-b-010 f-c-bo-neu025 ] has-border'
            placeholder='E-Mail-Adresse'
            inputProps={{
              'data-sel': 'ipt-email',
              inputMode: 'email',
              disabled: Boolean(UA.isLoggedIn() && email)
            }}
          />
          <Form.Phone
            className='[ f-c-ba-whi f-c-fo-pri f-d-bl f-m-b-020 f-c-bo-neu025 ] has-border'
            phoneName='phone'
            countryName='phoneCountryPrefix'
            placeholder='Handynummer'
            inputProps={{ 'data-sel': 'ipt-pnum', inputMode: 'tel' }}
            phoneCountryPrefixProps={{ 'data-sel': 'slct-pctry' }}
          />
          <Form.Checkbox
            label='Bitte informieren Sie mich zu Hausbau-Neuigkeiten und speziellen Angeboten'
            theme='form'
            name='receive_newsletter'
            labelClassNames='[ f-t-mini f-c-fo-pri ]'
            className='[ f-d-bl f-m-b-020 ]'
            inputProps={{ 'data-sel': 'ckbx-nwsl' }}
            wtData={{
              checkbox: {
                location: 'modal-email-phone-newsletter',
                ...props.data.trackingInfo
              }
            }}
          />
        </div>
        {props.requestType !== WIZARD_REQUEST_TYPES.outfitter &&
          isMobileView &&
          disclaimerParagraph}
      </div>
      <div className='RequestWizard-bottom'>
        <CTA
          type='block-primary'
          tag='button'
          buttonType='submit'
          data-sel='btn-cta'
          className='[ f-m-h-auto f-m-t-020 ] is-block:s'
          wtData={{
            'CTA-click': {
              ...props.data.trackingInfo
            }
          }}>
          {ctaText}
        </CTA>
        {props.requestType !== WIZARD_REQUEST_TYPES.outfitter &&
          !isMobileView &&
          disclaimerParagraph}
      </div>
    </Form.Wrapper>
  )
}

export default UserEmailPhone
